import type { ColumnMappings, Subsidiary } from '@revelio/core';

/**
 * Column keys of the subtree rooted at `company`, including the company's own
 * column. Columns are keyed by `Subsidiary.id`, so the walk uses `id` — even
 * though company identity everywhere else in reconciliation is by `rcid`.
 */
const collectSubtreeColumnKeys = (
  mappings: ColumnMappings,
  company: Subsidiary,
  acc: Set<string>
): void => {
  const childColumn = mappings[company.id];
  if (childColumn === undefined) return;
  // Column keys are strings; Subsidiary.id is typed string | number.
  acc.add(String(company.id));
  for (const child of childColumn.subsidiaries) {
    collectSubtreeColumnKeys(mappings, child, acc);
  }
};

/**
 * Merge a previously-saved subsidiary hierarchy with the company set the user
 * currently has selected, so reopening the editor preserves their restructuring.
 *
 * - Companies still selected keep their saved subtree (survivors).
 * - Companies no longer selected are dropped, along with their subtree columns.
 * - A selected company that no longer has a selected parent is promoted to the
 *   top level — including one that previously existed only as a nested
 *   subsidiary of a now-dropped parent. It must never be silently lost just
 *   because its rcid still appears in a discarded branch of the saved tree.
 * - A selection that is still reachable under a surviving parent is left in
 *   place — the saved structure wins over re-adding it at the top level.
 *
 * Identity is matched on `rcid`, never `id`: top-level companies are keyed by
 * rcid-string while nested/tree-picked ones are keyed by a name-derived id.
 */
export const reconcileSubsidiaryMappings = (
  saved: ColumnMappings,
  currentSelections: Subsidiary[]
): ColumnMappings => {
  const savedPrimary = saved.primary?.subsidiaries ?? [];
  const currentRcids = new Set(
    currentSelections.map((company) => company.rcid)
  );

  const survivingPrimary = savedPrimary
    .filter((company) => currentRcids.has(company.rcid))
    .map((company) => ({ ...company, isSelected: true }));

  // Subtree columns of top-level companies that are no longer selected — these,
  // and everything beneath them, are dropped.
  const droppedColumnKeys = new Set<string>();
  for (const company of savedPrimary) {
    if (!currentRcids.has(company.rcid)) {
      collectSubtreeColumnKeys(saved, company, droppedColumnKeys);
    }
  }

  // Carry over the retained columns and, in the same pass, collect every rcid
  // still reachable in the surviving tree.
  const reconciled: ColumnMappings = {};
  const survivingRcids = new Set<number>(
    survivingPrimary.map((company) => company.rcid)
  );
  for (const [key, column] of Object.entries(saved)) {
    if (key === 'primary' || droppedColumnKeys.has(key)) continue;
    reconciled[key] = column;
    for (const subsidiary of column.subsidiaries) {
      survivingRcids.add(subsidiary.rcid);
    }
  }

  // A selection not reachable in the surviving tree is genuinely new — including
  // a company that only existed under a now-dropped parent, which is promoted to
  // the top level rather than silently lost.
  const newPrimary = currentSelections
    .filter((company) => !survivingRcids.has(company.rcid))
    .map((company) => ({ ...company, isSelected: true }));

  const primarySubsidiaries = [...survivingPrimary, ...newPrimary];

  reconciled.primary = {
    title: saved.primary?.title ?? 'Primary',
    name: saved.primary?.name ?? 'Primary',
    isLoading: saved.primary?.isLoading ?? false,
    subsidiaries: primarySubsidiaries,
    selectAll:
      primarySubsidiaries.length > 0 &&
      primarySubsidiaries.every((company) => company.isSelected),
  };

  return reconciled;
};
